import { IComp } from '@app/models/Comp'
import { ITransactionHistory } from '@app/models/TransactionHistory'
import { errorToast, successToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, takeLatest } from 'redux-saga/effects'

// -------------------------
// STATE
// -------------------------

export interface ITransactionState {
  readonly stateValue: string
}

const StateRecord = Record({
  stateValue: '',
})

export class TransactionState extends StateRecord implements ITransactionState {
  public static fromJS(initialState?: Partial<ITransactionState>) {
    return new TransactionState(initialState)
  }

  public readonly stateValue: string

  constructor(props?: Partial<TransactionState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  EditTransaction = 'TRANSACTION::EDIT_TRANSACTION',
  ResetState = 'TRANSACTION::RESET_STATE',
  SaveTransaction = 'TRANSACTION::SAVE_TRANSACTION',
  SetStateValue = 'TRANSACTION::SET_STATE_VALUE',
}

interface IEditTransactionAction {
  done: (e: ITransactionHistory) => void
  persistToServer: boolean
  propertyId: string
  transaction: ITransactionHistory
  type: ActionTypes.EditTransaction
}

interface IResetStateAction {
  type: ActionTypes.ResetState
}

interface ISaveTransactionAction {
  comp?: IComp
  done: (e: ITransactionHistory) => void
  persistToServer: boolean
  propertyId: string
  transaction: ITransactionHistory
  type: ActionTypes.SaveTransaction
}

interface ISetStateValueAction {
  type: ActionTypes.SetStateValue
  value: string
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction = IResetStateAction | ISetStateValueAction

// -------------------------
// ACTION CREATORS
// -------------------------
function editTransaction(
  persistToServer: boolean,
  propertyId: string,
  transaction: ITransactionHistory,
  done: (e: ITransactionHistory) => void,
): IEditTransactionAction {
  return {
    done,
    persistToServer,
    propertyId,
    transaction,
    type: ActionTypes.EditTransaction,
  }
}

function resetState(): IResetStateAction {
  return { type: ActionTypes.ResetState }
}

function saveTransaction(
  persistToServer: boolean,
  propertyId: string,
  transaction: ITransactionHistory,
  done: (e: ITransactionHistory) => void,
  comp?: IComp,
): ISaveTransactionAction {
  return {
    comp,
    done,
    persistToServer,
    propertyId,
    transaction,
    type: ActionTypes.SaveTransaction,
  }
}

function setStateValue(value: string): ISetStateValueAction {
  return { type: ActionTypes.SetStateValue, value }
}

export const actionCreators = {
  editTransaction,
  saveTransaction,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<TransactionState> = (
  state: TransactionState = new TransactionState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.ResetState:
      return state.set('stateValue', '') as TransactionState

    case ActionTypes.SetStateValue:
      return state.set('stateValue', action.value) as TransactionState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------

function* saveTransactionSaga(action: ISaveTransactionAction) {
  try {
    if (action.persistToServer && action.comp) {
      const baseUrl = `/api/Properties/${action.propertyId}/TransactionHistory`

      const transactionOpts = {
        body: JSON.stringify(action.transaction),
        method: RequestMethod.POST,
      }
      const result = yield makeAuthenticatedRequest(baseUrl, transactionOpts)

      if (result.success === true) {
        yield put(
          successToast(
            'Transaction Added',
            `New transaction created for ${action.comp.propertyName}`,
          ),
        )
        action.done(action.transaction)
      }
    } else {
      action.done(action.transaction)
    }
  } catch (err) {
    yield put(errorToast('Oops!', `Could not create transaction`))
  }
}

function* watchSaveTransaction() {
  yield takeLatest(ActionTypes.SaveTransaction, saveTransactionSaga)
}

export function* transactionRootSaga() {
  yield fork(watchSaveTransaction)
}

// -------------------------
// SELECTORS
// -------------------------
